import { createLogger as me } from "@midi-volca-drum/utils";
import { WebMidi as C } from "webmidi";
import { create as Z } from "zustand";
import { jsx as L } from "react/jsx-runtime";
import { createContext as ee, useContext as te, useRef as U, useCallback as m, useEffect as k, useTransition as Ie, useState as Me, memo as B } from "react";
import { showToast as D, Slider as Ce, RadioGroup as Pe, Switch as be } from "@midi-volca-drum/ui-components";
const v = {
  MIN_VALUE: 0,
  MAX_VALUE: 127,
  CENTER_VALUE: 64,
  MIN_CHANNEL: 0,
  MAX_CHANNEL: 15
}, Xe = {
  log: () => {
  },
  error: () => {
  },
  warn: () => {
  },
  debug: () => {
  }
}, T = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  debug: console.debug.bind(console)
}, h = me(!1);
function ne(t) {
  return Number.isInteger(t) && t >= v.MIN_VALUE && t <= v.MAX_VALUE;
}
function ae(t) {
  return Number.isInteger(t) && t >= v.MIN_VALUE && t <= v.MAX_VALUE;
}
function re(t) {
  return Number.isInteger(t) && t >= v.MIN_CHANNEL && t <= v.MAX_CHANNEL;
}
function Je(t) {
  return Math.max(v.MIN_VALUE, Math.min(v.MAX_VALUE, t));
}
function Ke(t) {
  return Math.max(v.MIN_CHANNEL, Math.min(v.MAX_CHANNEL, t));
}
function oe(t) {
  return t === 127 ? 255 : t * 2;
}
function se(t) {
  return t === 127 ? 100 : Math.round(Math.round((t - 64) / 64 * 200) / 2);
}
function ie(t) {
  return t < 64 ? "Off" : "On";
}
function ce(t) {
  const n = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"], e = t === 0 ? 0 : t - 1, a = Math.floor(e / 12) - 1, r = e % 12;
  return (n[r] || "C") + a;
}
const ge = {
  doubler: oe,
  hundred: se,
  onoffer: ie,
  pitchnote: ce
}, Qe = {
  doubler: oe,
  hundred: se,
  onoffer: ie,
  pitchnote: ce
};
function qe(t) {
  return ge[t];
}
function ve(t, n) {
  const e = t.name.replace(/[^a-z0-9]/gi, "_").toLowerCase(), a = (n == null ? void 0 : n.partNumber) ?? 1;
  return (n == null ? void 0 : n.suggestedName) ?? `part${a}_${e}.json`;
}
async function Se(t, n, e = T) {
  const a = JSON.stringify(t, null, 2), r = ve(t, n);
  if (window.showSaveFilePicker)
    try {
      const l = await (await window.showSaveFilePicker({
        suggestedName: r,
        types: [
          {
            description: "JSON Files",
            accept: { "application/json": [".json"] }
          }
        ]
      })).createWritable();
      await l.write(a), await l.close(), e.log("Saved part data:", t);
      return;
    } catch (c) {
      c instanceof DOMException && c.name !== "AbortError" && e.error("Save failed:", c);
      return;
    }
  const o = new Blob([a], { type: "application/json" }), i = URL.createObjectURL(o), s = document.createElement("a");
  s.href = i, s.download = r, document.body.appendChild(s), s.click(), document.body.removeChild(s), URL.revokeObjectURL(i), e.log("Saved part data:", t);
}
function le(t, n = T) {
  try {
    return JSON.parse(t);
  } catch {
    try {
      const e = atob(t);
      return JSON.parse(e);
    } catch (e) {
      return n.error("Failed to parse part data:", e), null;
    }
  }
}
async function Ze(t, n = T) {
  try {
    const e = await t.text(), a = le(e, n);
    return a ? (n.log("Loaded part data:", a), a) : (n.error("Failed to parse part data from file"), null);
  } catch (e) {
    return n.error("Failed to load part data from file:", e), null;
  }
}
class we {
  constructor(n = T) {
    this.stateCallbacks = /* @__PURE__ */ new Set(), this.connectedCallbacks = /* @__PURE__ */ new Set(), this.disconnectedCallbacks = /* @__PURE__ */ new Set(), this.logger = n;
  }
  isSupported() {
    return C.supported;
  }
  isEnabled() {
    return C.enabled;
  }
  async initialize(n = {}) {
    if (C.enabled) {
      this.logger.log("WebMidi already enabled");
      return;
    }
    if (!C.supported)
      throw new Error("Web MIDI API is not supported in this browser");
    try {
      await C.enable({ sysex: n.sysex ?? !1 }), this.logger.log("WebMidi enabled successfully"), C.addListener("connected", (e) => {
        this.logger.log("MIDI device connected:", e.port.name), this.notifyStateChange(), e.port.type === "output" && this.connectedCallbacks.forEach((a) => a(e.port));
      }), C.addListener("disconnected", (e) => {
        this.logger.log("MIDI device disconnected:", e.port.name), this.notifyStateChange(), e.port.type === "output" && this.disconnectedCallbacks.forEach((a) => a(e.port));
      }), this.notifyStateChange();
    } catch (e) {
      throw this.logger.error("Failed to enable WebMidi:", e), e;
    }
  }
  async disable() {
    if (C.enabled)
      try {
        await C.disable(), this.logger.log("WebMidi disabled"), this.stateCallbacks.clear(), this.connectedCallbacks.clear(), this.disconnectedCallbacks.clear();
      } catch (n) {
        throw this.logger.error("Failed to disable WebMidi:", n), n;
      }
  }
  getOutputs() {
    return C.enabled ? C.outputs : (this.logger.warn("WebMidi not enabled, cannot get outputs"), []);
  }
  getOutput(n) {
    return this.getOutputs()[n] ?? null;
  }
  getFirstOutput() {
    return this.getOutput(0);
  }
  getConnectionState() {
    var o;
    const n = this.getOutputs(), e = n.length, a = e > 0, r = ((o = n[0]) == null ? void 0 : o.name) ?? null;
    return {
      isSupported: C.supported,
      hasAccess: C.enabled,
      deviceCount: e,
      isConnected: a,
      deviceName: r
    };
  }
  onStateChange(n) {
    return this.stateCallbacks.add(n), () => {
      this.stateCallbacks.delete(n);
    };
  }
  onDeviceConnected(n) {
    return this.connectedCallbacks.add(n), () => {
      this.connectedCallbacks.delete(n);
    };
  }
  onDeviceDisconnected(n) {
    return this.disconnectedCallbacks.add(n), () => {
      this.disconnectedCallbacks.delete(n);
    };
  }
  notifyStateChange() {
    const n = this.getConnectionState();
    this.stateCallbacks.forEach((e) => e(n));
  }
}
const E = new we();
function j(t, n, e, a, r = T) {
  if (!t)
    return r.warn("No MIDI output available"), !1;
  if (!re(n))
    return r.error(`Invalid MIDI channel: ${n} (must be 0-15)`), !1;
  if (!ne(e))
    return r.warn(`Invalid CC number: ${e} (must be 0-127)`), !1;
  if (!ae(a))
    return r.warn(`Invalid CC value: ${a} for CC ${e} (must be 0-127)`), !1;
  try {
    const o = n + 1, i = t.channels[o];
    return i ? (i.sendControlChange(e, a), !0) : (r.error(`Invalid MIDI channel: ${o}`), !1);
  } catch (o) {
    return r.error(`Failed to send CC ${e}:`, o), !1;
  }
}
function Ee(t, n, e, a = T) {
  if (!t)
    return a.warn("No MIDI output available"), 0;
  if (!re(n))
    return a.error(`Invalid MIDI channel: ${n} (must be 0-15)`), 0;
  const r = n + 1, o = t.channels[r];
  if (!o)
    return a.error(`Invalid MIDI channel: ${r}`), 0;
  let i = 0;
  return Object.entries(e).forEach(([s, c]) => {
    const l = parseInt(s, 10);
    if (isNaN(l) || !ne(l)) {
      a.warn(`Invalid CC number: ${s}`);
      return;
    }
    if (!ae(c)) {
      a.warn(`Invalid CC value: ${c} for CC ${l}`);
      return;
    }
    try {
      o.sendControlChange(l, c), i++;
    } catch (u) {
      a.error(`Failed to send CC ${l}:`, u);
    }
  }), a.log(`Sent ${i} CC messages to channel ${r}`), i;
}
function ye(t, n, e, a = T) {
  return t ? !n || !n.cc ? (a.error("Invalid part data: missing cc values"), 0) : (a.log(`Sending patch "${n.name}" to channel ${e + 1}`), Ee(t, e, n.cc, a)) : (a.warn("No MIDI output available"), 0);
}
const Ae = {
  PARAMETER_DISPLAY_TIMEOUT: 2e3,
  PART_RESTORE_DELAY: 50
}, z = {
  MIN: 1,
  MAX: 6,
  WAVEGUIDE: 0
};
function X(t) {
  return t >= z.MIN && t <= z.MAX;
}
const et = {
  MIDI_CHANNEL: 1,
  MODELS: [
    { id: "tube", label: "Tube", value: 0 },
    { id: "string", label: "String", value: 64 }
  ]
}, O = {
  ACTIVE: 1,
  NEAR_1: 0.6,
  NEAR_2: 0.3,
  NEAR_3: 0.2,
  FADED: 0.15
}, $ = {
  PITCH_OFFSET: 1,
  NOTES_PER_OCTAVE: 12,
  OCTAVE_OFFSET: -1
}, tt = {
  PART_CODES: ["Digit1", "Digit2", "Digit3", "Digit4", "Digit5", "Digit6"],
  WAVEGUIDE_CODES: ["KeyW"],
  ESCAPE: "Escape",
  ENTER: "Enter",
  SPACE: " "
};
function Te(t) {
  if (t === 1)
    return "orange";
  if (t === 2)
    return "green";
}
function nt(t) {
  return {
    0: O.ACTIVE,
    1: O.NEAR_1,
    2: O.NEAR_2,
    3: O.NEAR_3
  }[t] ?? O.FADED;
}
const H = /* @__PURE__ */ new Set([14, 15]), de = ["waveSelect", "modType", "envType"];
function ue(t, n, e) {
  return de.reduce((a, r) => a + (r === t ? n : e(r)), 0);
}
const J = {
  layer1: {
    waveSelect: 14,
    envType: 14,
    modType: 14,
    level: 17,
    pitch: 26,
    egAttack: 20,
    egRelease: 23,
    modDepth: 29,
    modSpeed: 46
  },
  layer2: {
    waveSelect: 15,
    envType: 15,
    modType: 15,
    level: 18,
    pitch: 27,
    egAttack: 21,
    egRelease: 24,
    modDepth: 30,
    modSpeed: 47
  },
  processing: {
    waveGuideSend: 103,
    bitReduction: 49,
    fold: 50,
    drive: 51,
    dryGain: 52,
    pan: 10,
    pitchQuantize: 53
  },
  waveguide: {
    modelType: 116,
    decay: 117,
    body: 118,
    tune: 119
  }
}, _e = [
  { id: "sine", label: "Sine", value: 0, icon: "sine" },
  { id: "saw", label: "Saw", value: 26, icon: "saw" },
  { id: "noisehp", label: "Noise HP", value: 52, icon: "noise-hp" },
  { id: "noiselp", label: "Noise LP", value: 77, icon: "noise-lp" },
  { id: "noisebp", label: "Noise BP", value: 103, icon: "noise-bp" }
], Ne = [
  { id: "envelope", label: "Envelope", value: 0, icon: "envelope" },
  { id: "lfo", label: "LFO", value: 9, icon: "lfo" },
  { id: "random", label: "Random", value: 18, icon: "random" }
], at = [
  { id: "ad", label: "AD", value: 0, icon: "ad" },
  { id: "exp", label: "Exp", value: 3, icon: "exp" },
  { id: "multi", label: "Multi", value: 6, icon: "multi" }
], rt = [
  { id: "tube", label: "Tube", value: 0 },
  { id: "string", label: "String", value: 64 }
];
function Re(t) {
  const n = [..._e].map((s) => s.value).sort((s, c) => c - s), e = [...Ne].map((s) => s.value).sort((s, c) => c - s), a = n.find((s) => t >= s) ?? 0, r = t - a, o = e.find((s) => r >= s) ?? 0, i = r - o;
  return { waveSelect: a, modType: o, envType: i };
}
const Fe = {
  level: {
    label: "Level",
    min: 0,
    max: 127,
    default: 127,
    displayFunc: "doubler"
  },
  pitch: {
    label: "Pitch",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  egAttack: {
    label: "EG Attack",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  egRelease: {
    label: "EG Release",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  modDepth: {
    label: "Mod Depth",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "hundred"
  },
  modSpeed: {
    label: "Mod Speed",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  waveGuideSend: {
    label: "Wave Guide Send",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  bitReduction: {
    label: "Bit Reduction",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  fold: {
    label: "Fold",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  drive: {
    label: "Drive",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  dryGain: {
    label: "Dry Gain",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "hundred"
  },
  pan: {
    label: "Pan",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "hundred"
  },
  waveGuideDecay: {
    label: "Decay",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  waveGuideBody: {
    label: "Body",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  },
  waveGuideTune: {
    label: "Tune",
    min: 0,
    max: 127,
    default: 64,
    displayFunc: "doubler"
  }
}, ot = [
  {
    name: "Melodic sawtooth",
    cc: {
      10: 64,
      14: 38,
      15: 38,
      17: 127,
      18: 127,
      20: 0,
      21: 0,
      23: 94,
      24: 93,
      26: 61,
      27: 61,
      29: 68,
      30: 0,
      46: 15,
      47: 0,
      49: 0,
      50: 0,
      51: 0,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Basic clap",
    cc: {
      10: 64,
      14: 92,
      15: 92,
      17: 31,
      18: 44,
      20: 55,
      21: 46,
      23: 64,
      24: 63,
      26: 63,
      27: 90,
      29: 0,
      30: 0,
      46: 0,
      47: 0,
      49: 70,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "A1 snare",
    cc: {
      10: 64,
      14: 3,
      15: 76,
      17: 75,
      18: 127,
      20: 0,
      21: 43,
      23: 60,
      24: 61,
      26: 18,
      27: 78,
      29: 96,
      30: 90,
      46: 80,
      47: 127,
      49: 0,
      50: 0,
      51: 0,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Cowbell",
    cc: {
      10: 64,
      14: 35,
      15: 35,
      17: 61,
      18: 43,
      20: 0,
      21: 0,
      23: 68,
      24: 67,
      26: 33,
      27: 48,
      29: 68,
      30: 60,
      46: 1,
      47: 3,
      49: 32,
      50: 44,
      51: 67,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Basic Bass",
    cc: {
      10: 64,
      14: 3,
      15: 29,
      17: 127,
      18: 127,
      20: 25,
      21: 7,
      23: 76,
      24: 76,
      26: 41,
      27: 41,
      29: 83,
      30: 0,
      46: 44,
      47: 0,
      49: 0,
      50: 0,
      51: 0,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Scratch Clap",
    cc: {
      10: 64,
      14: 83,
      15: 112,
      17: 45,
      18: 46,
      20: 41,
      21: 78,
      23: 69,
      24: 57,
      26: 61,
      27: 62,
      29: 127,
      30: 101,
      46: 48,
      47: 40,
      49: 70,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Easy Clap",
    cc: {
      10: 64,
      14: 92,
      15: 92,
      17: 31,
      18: 44,
      20: 55,
      21: 46,
      23: 65,
      24: 64,
      26: 63,
      27: 90,
      29: 0,
      30: 0,
      46: 0,
      47: 0,
      49: 70,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Melodic Sine",
    cc: {
      10: 64,
      14: 12,
      15: 12,
      17: 127,
      18: 127,
      20: 0,
      21: 0,
      23: 92,
      24: 93,
      26: 73,
      27: 73,
      29: 67,
      30: 0,
      46: 15,
      47: 0,
      49: 0,
      50: 0,
      51: 0,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Metallic Snare",
    cc: {
      10: 64,
      14: 0,
      15: 103,
      17: 59,
      18: 127,
      20: 0,
      21: 44,
      23: 57,
      24: 54,
      26: 49,
      27: 49,
      29: 70,
      30: 0,
      46: 61,
      47: 0,
      49: 127,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Heartbeat",
    cc: {
      10: 64,
      14: 3,
      15: 3,
      17: 127,
      18: 127,
      20: 55,
      21: 83,
      23: 75,
      24: 57,
      26: 32,
      27: 32,
      29: 94,
      30: 64,
      46: 73,
      47: 0,
      49: 0,
      50: 76,
      51: 43,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Metallic Closed HH",
    cc: {
      10: 64,
      14: 41,
      15: 76,
      17: 28,
      18: 127,
      20: 39,
      21: 22,
      23: 39,
      24: 36,
      26: 127,
      27: 127,
      29: 95,
      30: 100,
      46: 125,
      47: 127,
      49: 0,
      50: 0,
      51: 55,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Basic Shaker",
    cc: {
      10: 64,
      14: 70,
      15: 70,
      17: 127,
      18: 0,
      20: 50,
      21: 0,
      23: 55,
      24: 0,
      26: 50,
      27: 0,
      29: 81,
      30: 0,
      46: 127,
      47: 0,
      49: 0,
      50: 39,
      51: 12,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Double Shaker",
    cc: {
      10: 64,
      14: 70,
      15: 106,
      17: 127,
      18: 34,
      20: 37,
      21: 77,
      23: 50,
      24: 62,
      26: 124,
      27: 104,
      29: 0,
      30: 0,
      46: 0,
      47: 0,
      49: 0,
      50: 0,
      51: 0,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Rimshot",
    cc: {
      10: 64,
      14: 3,
      15: 3,
      17: 127,
      18: 63,
      20: 0,
      21: 0,
      23: 38,
      24: 37,
      26: 59,
      27: 49,
      29: 80,
      30: 80,
      46: 82,
      47: 82,
      49: 0,
      50: 95,
      51: 66,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Razor Hat Closed",
    cc: {
      10: 64,
      14: 47,
      15: 67,
      17: 50,
      18: 50,
      20: 0,
      21: 21,
      23: 37,
      24: 0,
      26: 127,
      27: 127,
      29: 104,
      30: 100,
      46: 127,
      47: 127,
      49: 127,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Razor Hat Open",
    cc: {
      10: 64,
      14: 47,
      15: 67,
      17: 50,
      18: 50,
      20: 0,
      21: 43,
      23: 43,
      24: 54,
      26: 127,
      27: 127,
      29: 104,
      30: 121,
      46: 127,
      47: 7,
      49: 127,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "High Conga",
    cc: {
      10: 64,
      14: 3,
      15: 103,
      17: 127,
      18: 50,
      20: 25,
      21: 53,
      23: 59,
      24: 55,
      26: 39,
      27: 19,
      29: 104,
      30: 75,
      46: 83,
      47: 67,
      49: 0,
      50: 127,
      51: 127,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Floor Tom",
    cc: {
      10: 64,
      14: 3,
      15: 52,
      17: 29,
      18: 30,
      20: 0,
      21: 0,
      23: 78,
      24: 36,
      26: 9,
      27: 107,
      29: 75,
      30: 54,
      46: 40,
      47: 11,
      49: 0,
      50: 127,
      51: 53,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  },
  {
    name: "Claves",
    cc: {
      10: 64,
      14: 9,
      15: 9,
      17: 63,
      18: 0,
      20: 15,
      21: 0,
      23: 30,
      24: 0,
      26: 118,
      27: 0,
      29: 80,
      30: 0,
      46: 46,
      47: 0,
      49: 0,
      50: 0,
      51: 0,
      52: 64,
      53: 127,
      103: 0,
      116: 0,
      117: 0,
      118: 0,
      119: 15
    }
  }
], Oe = Z((t) => ({
  connectionState: {
    isSupported: !1,
    hasAccess: !1,
    isConnected: !1,
    deviceName: null,
    deviceCount: 0
  },
  parameterDisplay: {
    show: !1,
    name: "",
    rawValue: 0,
    transformedValue: "",
    ccNumber: 0,
    pitchNotes: void 0
  },
  setConnectionState: (n) => t((e) => ({
    connectionState: { ...e.connectionState, ...n }
  })),
  setParameterDisplay: (n) => t((e) => ({
    parameterDisplay: { ...e.parameterDisplay, ...n, show: !0 }
  })),
  hideParameterDisplay: () => t((n) => ({
    parameterDisplay: { ...n.parameterDisplay, show: !1 }
  }))
}));
function V(t, n, e) {
  return `${t}-${n}-${e}`;
}
function K(t, n, e) {
  const a = t.get(e);
  return a ? { ...a, cc: { ...a.cc } } : { name: n.get(e) ?? `Part ${e}`, cc: {} };
}
const y = Z((t, n) => ({
  currentPart: 1,
  isPending: !1,
  partData: /* @__PURE__ */ new Map(),
  partNames: /* @__PURE__ */ new Map(),
  partModified: /* @__PURE__ */ new Map(),
  activePresets: /* @__PURE__ */ new Map(),
  parameterValues: /* @__PURE__ */ new Map(),
  setCurrentPart: (e) => t({ currentPart: e }),
  setPending: (e) => t({ isPending: e }),
  setPartCC: (e, a, r, o) => {
    const i = n(), s = new Map(i.partData), c = K(i.partData, i.partNames, e), l = new Map(i.partModified);
    if (l.set(e, !0), o) {
      const u = new Map(i.parameterValues);
      u.set(V(e, a, o), r), H.has(a) ? c.cc[a.toString()] = ue(
        o,
        r,
        (p) => u.get(V(e, a, p)) ?? 0
      ) : c.cc[a.toString()] = r, s.set(e, c), t({
        parameterValues: u,
        partData: s,
        partModified: l
      });
    } else
      c.cc[a.toString()] = r, s.set(e, c), t({ partData: s, partModified: l });
  },
  setPartCCBatch: (e, a) => {
    const r = n(), o = new Map(r.partData), i = K(r.partData, r.partNames, e);
    i.cc = { ...i.cc, ...a }, o.set(e, i), t({ partData: o });
  },
  getPartCC: (e, a, r) => {
    if (r)
      return n().parameterValues.get(V(e, a, r));
    const o = n().partData.get(e);
    return o == null ? void 0 : o.cc[a.toString()];
  },
  getPartData: (e) => n().partData.get(e),
  setPartName: (e, a) => {
    const r = n(), o = new Map(r.partNames);
    o.set(e, a);
    const i = new Map(r.partData), s = i.get(e);
    s && (s.name = a, i.set(e, s)), t({ partNames: o, partData: i });
  },
  getPartName: (e) => {
    const a = n().partNames.get(e);
    return a !== void 0 ? a : `Part ${e}`;
  },
  setPartModified: (e, a) => {
    const r = new Map(n().partModified);
    r.set(e, a), t({ partModified: r });
  },
  isPartModified: (e) => n().partModified.get(e) || !1,
  clearPartData: (e) => {
    const a = n(), r = new Map(a.partData);
    r.delete(e);
    const o = new Map(a.partNames);
    o.delete(e);
    const i = new Map(a.partModified);
    i.set(e, !1);
    const s = new Map(a.activePresets);
    s.delete(e), t({
      partData: r,
      partNames: o,
      partModified: i,
      activePresets: s
    });
  },
  setActivePreset: (e, a) => {
    const r = n(), o = new Map(r.activePresets);
    a === null ? o.delete(e) : o.set(e, a), t({ activePresets: o });
  },
  getActivePreset: (e) => n().activePresets.get(e),
  loadPartData: (e, a) => {
    const r = n(), o = new Map(r.partData);
    o.set(e, { ...a, cc: { ...a.cc } });
    const i = new Map(r.partNames);
    i.set(e, a.name);
    const s = new Map(r.parameterValues);
    for (const c of H) {
      const l = a.cc[c.toString()];
      if (l !== void 0) {
        const u = Re(l);
        for (const p of de)
          s.set(V(e, c, p), u[p]);
      }
    }
    t({ partData: o, partNames: i, parameterValues: s });
  }
})), st = (t) => t.partNames, Q = {
  LAYER_1_PITCH: J.layer1.pitch,
  LAYER_2_PITCH: J.layer2.pitch
};
function Le(t) {
  return t === Q.LAYER_1_PITCH || t === Q.LAYER_2_PITCH;
}
function xe(t) {
  const n = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"], e = [];
  for (let a = 0; a <= 127; a++) {
    const r = a === 0 ? 0 : a - $.PITCH_OFFSET, o = Math.floor(r / $.NOTES_PER_OCTAVE) + $.OCTAVE_OFFSET, i = r % $.NOTES_PER_OCTAVE, s = (n[i] || "C") + o;
    e.push({
      note: s,
      ccValue: a,
      isActive: a === t
    });
  }
  return e;
}
const pe = ee(null);
function it({ children: t, config: n }) {
  const e = U(null), a = U(null), { setConnectionState: r, setParameterDisplay: o, hideParameterDisplay: i } = Oe(), {
    getPartData: s,
    getPartName: c,
    setPartName: l,
    clearPartData: u,
    getActivePreset: p,
    loadPartData: I
  } = y(), M = m(
    (d, f, S, F) => {
      a.current && clearTimeout(a.current);
      const G = Le(F) ? xe(f) : void 0;
      o({
        show: !0,
        name: d,
        rawValue: f,
        transformedValue: S,
        ccNumber: F,
        pitchNotes: G
      }), a.current = setTimeout(() => {
        i();
      }, Ae.PARAMETER_DISPLAY_TIMEOUT);
    },
    [o, i]
  );
  k(() => (h.log("Initializing MIDI Device Manager..."), E.initialize({ sysex: !1 }).then(() => {
    h.log("MIDI Device Manager initialized successfully");
    const d = E.getConnectionState();
    r(d);
    const f = E.getFirstOutput();
    f ? (e.current = f, h.log("Selected MIDI output:", f.name)) : (h.warn("No MIDI outputs available"), D.info("No MIDI devices found. Please connect your Volca Drum."));
    const S = E.onStateChange((g) => {
      h.log("MIDI state changed:", g), r(g);
    }), F = E.onDeviceConnected((g) => {
      h.log("MIDI device connected:", g.name), e.current || (e.current = g, D.success(`Connected to ${g.name}`));
    }), G = E.onDeviceDisconnected((g) => {
      var Y;
      h.log("MIDI device disconnected:", g.name), ((Y = e.current) == null ? void 0 : Y.id) === g.id && (e.current = null, D.error(`Disconnected from ${g.name}`));
    });
    return () => {
      S(), F(), G();
    };
  }).catch((d) => {
    var f;
    h.error("Failed to initialize MIDI Device Manager:", d), (f = d.message) != null && f.includes("not supported") ? D.error("MIDI not supported in this browser") : d.name === "SecurityError" ? D.error("MIDI access denied. Please grant permissions.") : D.error("Failed to access MIDI devices"), r({
      isSupported: E.isSupported(),
      hasAccess: !1,
      deviceCount: 0,
      isConnected: !1,
      deviceName: null
    });
  }), () => {
    a.current && clearTimeout(a.current), E.disable().catch((d) => {
      h.error("Error disabling MIDI Device Manager:", d);
    });
  }), [M, r]);
  const P = m((d, f, S) => {
    if (!e.current) {
      h.warn("MIDI output not available"), D.error("MIDI not connected");
      return;
    }
    j(e.current, d, f, S, h) || D.error("Failed to send MIDI message");
  }, []), _ = m((d, f) => {
    if (!e.current) {
      h.warn("MIDI output not available"), D.error("MIDI not connected");
      return;
    }
    for (let S = 0; S < 6; S++)
      j(e.current, S, d, f, h);
  }, []), w = m(() => {
    if (!e.current) {
      h.warn("MIDI output not available"), D.error("MIDI not connected");
      return;
    }
    try {
      const d = y.getState().currentPart;
      if (d < 1 || d > 6) {
        h.warn("Invalid part selected");
        return;
      }
      const f = s(d);
      if (!f || Object.keys(f.cc).length === 0) {
        D.info("No data to send for this part");
        return;
      }
      ye(e.current, f, d - 1), D.success("Patch sent to device");
    } catch (d) {
      h.error("Failed to send patch:", d), D.error("Failed to send patch to device");
    }
  }, [s]), b = m(
    (d) => c(d),
    [c]
  ), A = m(
    (d, f) => {
      X(d) && l(d, f);
    },
    [l]
  ), N = m(
    (d) => {
      if (!X(d))
        return;
      const f = p(d);
      f ? (I(d, f), D.success(`Part ${d} reset to "${f.name}"`)) : (u(d), D.success(`Part ${d} reset`));
    },
    [u, p, I]
  ), R = m(() => e.current, []), De = {
    sendMessage: P,
    sendBroadcast: _,
    sendCurrentPatch: w,
    showParameterDisplay: M,
    getPartName: b,
    updatePartName: A,
    resetPart: N,
    getOutput: R,
    config: n
  };
  return /* @__PURE__ */ L(pe.Provider, { value: De, children: t });
}
function x() {
  const t = te(pe);
  if (!t)
    throw new Error("useMIDI must be used within MIDIProvider");
  return t;
}
const fe = ee(null);
function ct({ children: t }) {
  const [n, e] = Ie(), { currentPart: a, setCurrentPart: r, setPending: o } = y();
  k(() => {
    o(n);
  }, [n, o]);
  const i = m(
    (l) => {
      if (l < 1 || l > 6) {
        h.warn(`Invalid part number: ${l}`);
        return;
      }
      h.log(`Switching to part ${l}`), e(() => {
        r(l);
      });
    },
    [r]
  ), s = m(() => {
    h.log("Switching to waveguide view"), e(() => {
      r(0);
    });
  }, [r]), c = {
    currentPart: a,
    isPending: n,
    switchPart: i,
    switchToWaveguide: s
  };
  return /* @__PURE__ */ L(fe.Provider, { value: c, children: t });
}
function lt() {
  const t = te(fe);
  if (!t)
    throw new Error("usePart must be used within PartProvider");
  return t;
}
function $e() {
  const { sendMessage: t } = x(), { setPartCC: n, getPartCC: e } = y();
  return m(
    (a, r, o, i) => {
      let s = o;
      H.has(r) && i && (s = ue(
        i,
        o,
        (c) => e(a, r, c) ?? 0
      )), t(a - 1, r, s), n(a, r, o, i);
    },
    [t, n, e]
  );
}
function he(t, n, e = !1) {
  const a = U(n);
  k(() => {
    e && (a.current = n);
  }, [n, e]), k(() => {
    let r = t(y.getState());
    return (e ? a.current : n)(r), y.subscribe((s) => {
      const c = t(s);
      c !== r && ((e ? a.current : n)(c), r = c);
    });
  }, [t, n, e]);
}
function Ve(t, n, e, a) {
  const r = m(
    (o) => o.getPartCC(t, n, a) ?? (a ? 0 : 64),
    [t, n, a]
  );
  he(r, e, !0);
}
function W(t, n, e, a, r, o) {
  const { sendBroadcast: i } = x(), { setPartCC: s } = y(), c = $e(), [l, u] = Me(e);
  Ve(t, n, u, r);
  const p = m(
    (I) => {
      u(I), o ? (i(n, I), s(t, n, I, r)) : c(t, n, I, r), a == null || a(I);
    },
    [t, n, o, i, s, c, a, r]
  );
  return { value: l, handleChange: p };
}
function dt(t, n) {
  const e = m(
    (a) => a.getPartName(t),
    [t]
  );
  he(e, n, !1);
}
function ut(t) {
  const { getPartData: n, loadPartData: e } = y(), a = U(null), r = m(async () => {
    if (t === 0)
      return;
    const s = n(t);
    if (!s) {
      D.error("No data to save for this part");
      return;
    }
    try {
      await Se(s, { partNumber: t }), D.success(`Part ${t} saved`);
    } catch (c) {
      h.error("Failed to save part:", c), D.error("Failed to save part");
    }
  }, [t, n]), o = m(() => {
    var s;
    (s = a.current) == null || s.click();
  }, []), i = m(
    (s) => {
      var u;
      const c = (u = s.target.files) == null ? void 0 : u[0];
      if (!c || t === 0)
        return;
      const l = new FileReader();
      l.onload = (p) => {
        var M;
        const I = (M = p.target) == null ? void 0 : M.result;
        try {
          const P = le(I);
          if (!P) {
            D.error("Invalid part data format");
            return;
          }
          e(t, P), D.success(`Part ${t} loaded`);
        } catch (P) {
          h.error("Failed to load part:", P), D.error("Failed to load part file");
        }
      }, l.readAsText(c), s.target.value = "";
    },
    [t, e]
  );
  return {
    fileInputRef: a,
    savePart: r,
    loadPart: o,
    handleFileChange: i
  };
}
const Ue = B(
  ({
    id: t,
    label: n,
    cc: e,
    midiChannel: a,
    min: r = 0,
    max: o = 127,
    value: i,
    displayFunc: s = null,
    color: c,
    onChange: l,
    broadcast: u
  }) => {
    const { config: p, showParameterDisplay: I } = x(), M = m(
      (b) => {
        var A;
        if (s && ((A = p == null ? void 0 : p.parameterDisplayTransforms) != null && A[s])) {
          const N = p.parameterDisplayTransforms[s], R = N(b);
          return typeof R == "string" ? R : R.toString();
        }
        return b.toString();
      },
      [s, p]
    ), P = m(
      (b) => {
        I(n, b, M(b), e), l == null || l(b);
      },
      [n, e, I, M, l]
    ), { value: _, handleChange: w } = W(
      a,
      e,
      i ?? 64,
      P,
      void 0,
      u
    );
    return /* @__PURE__ */ L(
      Ce,
      {
        id: t,
        label: n,
        min: r,
        max: o,
        value: _,
        onChange: w,
        formatValue: M,
        color: c
      }
    );
  }
);
Ue.displayName = "MIDISlider";
function pt(t, n, e, a, r, o) {
  const i = Fe[n], s = r ? `l${r}` : "";
  return {
    id: `p${t}${s}${n}`,
    label: i.label,
    cc: e,
    midiChannel: a,
    min: i.min,
    max: i.max,
    value: i.default,
    displayFunc: i.displayFunc,
    layerNum: r,
    color: o || Te(r)
  };
}
function ft(t, n) {
  return t.map((e) => ({
    id: `${n}-${e.id}`,
    label: e.label,
    value: e.value,
    icon: e.icon,
    variant: "icon"
  }));
}
function q(t, n = 10, e = 0) {
  const a = typeof t == "string" ? parseInt(t, n) : t;
  return isNaN(a) ? e : a;
}
const ke = B(
  ({
    groupName: t,
    options: n,
    selectedValue: e = 0,
    midiChannel: a,
    cc: r,
    color: o,
    label: i,
    onChange: s,
    className: c,
    paramName: l,
    broadcast: u
  }) => {
    const { showParameterDisplay: p } = x(), { value: I, handleChange: M } = W(
      a,
      r,
      e,
      s,
      l,
      u
    ), P = m(
      (_) => {
        var A;
        const w = q(_), b = ((A = n.find((N) => q(N.value) === w)) == null ? void 0 : A.label) ?? String(w);
        p(i ?? t, w, b, r), M(w);
      },
      [i, t, r, n, p, M]
    );
    return /* @__PURE__ */ L(
      Pe,
      {
        name: t,
        options: n,
        value: I,
        onChange: P,
        color: o,
        label: i,
        className: c
      }
    );
  }
);
ke.displayName = "MIDIRadioGroup";
const Ge = B(
  ({ id: t, label: n, cc: e, midiChannel: a, onValue: r = 127, offValue: o = 0, color: i, onChange: s, paramName: c }) => {
    const { showParameterDisplay: l } = x(), { value: u, handleChange: p } = W(
      a,
      e,
      o,
      s,
      c
    ), I = m(() => {
      const M = u === o ? r : o;
      l(n, M, M === r ? "ON" : "OFF", e), p(M);
    }, [u, r, o, n, e, l, p]);
    return /* @__PURE__ */ L(
      be,
      {
        id: t,
        label: n,
        checked: u !== o,
        onChange: I,
        color: i
      }
    );
  }
);
Ge.displayName = "MIDISwitch";
export {
  J as CC_NUMBERS,
  at as ENVELOPE_TYPES,
  tt as KEYBOARD_SHORTCUTS,
  H as LAYER_TYPE_CCS,
  de as LAYER_TYPE_PARAMS,
  we as MIDIDeviceManager,
  it as MIDIProvider,
  ke as MIDIRadioGroup,
  Ue as MIDISlider,
  Ge as MIDISwitch,
  v as MIDI_CONSTANTS,
  $ as MIDI_NOTE,
  Ne as MODULATION_TYPES,
  Fe as PARAMETERS,
  Qe as PARAMETER_DISPLAY_TRANSFORMS,
  z as PART_NUMBERS,
  Q as PITCH_CC_NUMBERS,
  O as PITCH_NOTE_OPACITY,
  ct as PartProvider,
  _e as SOUND_SOURCES,
  Ae as TIMING,
  ot as VOLCA_DRUM_PRESETS,
  et as WAVEGUIDE,
  rt as WAVEGUIDE_MODEL_TYPES,
  ft as buildRadioOptions,
  pt as buildSliderConfig,
  nt as calculatePitchNoteOpacity,
  Ke as clampChannel,
  Je as clampMIDIValue,
  T as consoleLogger,
  Re as decodeLayerTypeCC,
  ge as displayTransforms,
  ue as encodeLayerTypeCC,
  xe as generatePitchNotes,
  Te as getLayerColor,
  qe as getTransform,
  Le as isPitchParameter,
  ne as isValidCC,
  re as isValidChannel,
  ae as isValidMIDIValue,
  X as isValidPartNumber,
  Ze as loadPartDataFromFile,
  h as logger,
  E as midiDeviceManager,
  Xe as noopLogger,
  le as parsePartData,
  Se as savePartDataToFile,
  st as selectPartNames,
  j as sendCC,
  Ee as sendCCBatch,
  ye as sendPartPatch,
  q as toNumber,
  x as useMIDI,
  W as useMIDIControl,
  $e as useMIDISend,
  Oe as useMIDIStore,
  lt as usePart,
  Ve as usePartDataListener,
  ut as usePartFileOperations,
  dt as usePartNameListener,
  y as usePartStore,
  he as useStoreListener
};
//# sourceMappingURL=index.js.map
